import {HttpErrorResponse, HttpInterceptorFn, HttpResponse} from '@angular/common/http';
import {inject, Injector} from '@angular/core';
import {Router} from '@angular/router';
import {throwError} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';

import {ApiResponse} from '@app/core/api';
import {ErrorCode} from '@app/core/enums';
import {TokenRefreshService, TokenService} from '@app/core/services/base';
import {HttpErrorStatus} from '@app/core/enums';
import {AppNotifications} from '@shared/enums/app-notifications.enum';
import {BootstrapAppService} from '@shared/services/bootstrap-app.service';
import {ToastifyService} from '@shared/services/toastify.service';
import {WorkspaceService} from '@shared/services/workspace.service';
import {environment} from '@env/environment';

const apiUrl = environment.apiUrl?.replace(/\/$/, '') ?? '';
const isApiRequest = (url: string) => !!apiUrl && url.startsWith(apiUrl);
const isRefreshRequest = (url: string) => url.slice(-5) === 'token' || url.includes('/auth/token');
const isJsonConfigRequest = (url: string) => url.includes('version.json') || url.includes('.json');

export const apiResponseInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url)) {
    return next(req);
  }
  const toastifyService = inject(ToastifyService);
  const tokenRefreshService = inject(TokenRefreshService);
  const tokenService = inject(TokenService);
  const router = inject(Router);
  const workspaceService = inject(WorkspaceService);
  const injector = inject(Injector);
  return next(req).pipe(
    map((event) => {
      if (!(event instanceof HttpResponse) || isJsonConfigRequest(req.url)) {
        return event;
      }
      const body = event.body;
      if (typeof body === 'object' && body !== null && event.status === 200 && 'success' in body && !(body as ApiResponse<unknown>).success) {
        throw new HttpErrorResponse({
          headers: event.headers,
          url: event.url ?? undefined,
          status: event.status,
          error: body,
          statusText: 'OK',
        });
      }
      return event;
    }),
    catchError((error: HttpErrorResponse) => handleApiError(error, req, next, {
      toastifyService,
      tokenRefreshService,
      tokenService,
      router,
      workspaceService,
      injector,
    })),
  );
};

function handleApiError(
  error: HttpErrorResponse,
  req: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
  deps: {
    toastifyService: ToastifyService;
    tokenRefreshService: TokenRefreshService;
    tokenService: TokenService;
    router: Router;
    workspaceService: WorkspaceService;
    injector: Injector;
  },
) {
  const {toastifyService, tokenRefreshService, tokenService, router, workspaceService, injector} = deps;
  if (error.status === HttpErrorStatus.Unauthorized) {
    if (!isRefreshRequest(req.url) && tokenRefreshService.hasRefreshToken()) {
      return tokenRefreshService.refresh().pipe(
        switchMap((accessToken) => next(req.clone({
          setHeaders: {Authorization: `Bearer ${accessToken}`},
        })).pipe(
          catchError((retryError: HttpErrorResponse) => {
            if (retryError.status === HttpErrorStatus.Unauthorized) {
              tokenService.logOut();
              toastifyService.error(AppNotifications.LoginAgain);
            }
            return throwError(() => retryError);
          }),
        )),
      );
    }
    tokenService.logOut();
    toastifyService.error(AppNotifications.LoginAgain);
    return throwError(() => error);
  }
  if (error.status === 200 || error.status === 422) {
    const response = error.error as ApiResponse<null, number, string> | undefined;
    if (response?.description) {
      toastifyService.error(response.description);
    }
    return throwError(() => error);
  }
  if (error.status === HttpErrorStatus.NotFound) {
    toastifyService.error(AppNotifications.UnknownError);
    void router.navigate(['/404']);
    return throwError(() => error);
  }
  if (error.status === HttpErrorStatus.Forbidden) {
    if (error.error?.errorCode === ErrorCode.WorkspaceAccessDenied && workspaceService.isAssignedWorkspace()) {
      injector.get(BootstrapAppService).revertToPersonalAndRefetch().subscribe();
    } else {
      toastifyService.error(AppNotifications.NoAccess);
    }
    return throwError(() => error);
  }
  if (error.status === 429) {
    toastifyService.error(AppNotifications.TooManyRequests);
    return throwError(() => error);
  }
  toastifyService.error(AppNotifications.UnknownError);
  return throwError(() => error);
}
